/**
 * How an approved revision reads: its lifecycle, and whether it is published.
 *
 * Shared by the portfolio row and the curriculum history under it, which
 * label the same revision and must not disagree about it.
 *
 * @module components/portfolio/revision-presentation
 */

import type { lifecycleTone } from "@/components/shared/status-tones";
import type { BadgeTone } from "@/components/ui";
import type { PortfolioProgrammeRevision } from "@/platform";
import { formatCalendarDate } from "@/utils/calendar-dates";
import { getRegion } from "@/utils/format-instant";
import type { PublicationStatus, PublicationStatusKind } from "@/utils/publication-status";

export const LIFECYCLE_LABEL: Record<string, string> = {
  current: "Current",
  "approved-future": "Approved future",
  superseded: "Superseded",
};

export const TONE_BADGE: Record<ReturnType<typeof lifecycleTone>, BadgeTone> = {
  success: "success",
  warning: "warning",
  error: "error",
  info: "primary",
  neutral: "neutral",
};

const STATUS_LABEL: Record<PublicationStatusKind, { label: string; tone: BadgeTone }> = {
  internal: { label: "Internal only", tone: "neutral" },
  scheduled: { label: "Scheduled", tone: "primary" },
  eligible: { label: "Not published", tone: "neutral" },
  ended: { label: "Ended", tone: "neutral" },
};

/** What a rule's publication status reads as, wherever a rule is listed. */
export function publicationStatusLabel(status: PublicationStatusKind): string {
  return STATUS_LABEL[status].label;
}

/** The badge tone a rule's publication status is painted in. */
export function publicationStatusTone(status: PublicationStatusKind): BadgeTone {
  return STATUS_LABEL[status].tone;
}

function isLive(revision: PortfolioProgrammeRevision): boolean {
  return Boolean(revision.latestPublication && !revision.latestPublication.withdrawnAt);
}

/** Whether a revision is published, withdrawn, or where its scope stands. */
export function publicationState(revision: PortfolioProgrammeRevision): {
  label: string;
  tone: BadgeTone;
} {
  if (isLive(revision)) {
    return { label: "Published", tone: "success" };
  }
  if (revision.latestPublication?.withdrawnAt) {
    return { label: "Withdrawn", tone: "warning" };
  }
  return STATUS_LABEL[revision.publicationStatus.status];
}

/** Whether a scope may be published: consent recorded and its dates not past. */
export function canBePublished(revision: PortfolioProgrammeRevision): boolean {
  const { status } = revision.publicationStatus;
  return status === "scheduled" || status === "eligible";
}

/** A status's reason, naming the day a scheduled scope becomes eligible. */
export function statusReason(
  { status, reason, from }: PublicationStatus,
  locale: string = getRegion().locale,
): string {
  return status === "scheduled" && from
    ? `Consent is recorded. Eligible to publish from ${formatCalendarDate(from, locale)}.`
    : reason;
}

/** Why a revision is not published, or null while it is. */
export function publicationReason(
  revision: PortfolioProgrammeRevision,
  locale: string = getRegion().locale,
): string | null {
  if (isLive(revision)) {
    return null;
  }
  return statusReason(revision.publicationStatus, locale);
}
